import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import {
  Grid,
  Paper,
  Typography,
  makeStyles,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Box,
  Container,
  Tabs,
  Tab,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Snackbar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@material-ui/core';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Contacts as ContactsIcon,
  Business as BusinessIcon,
  Home as HomeIcon,
  Assessment as AssessmentIcon,
  SyncAlt as SyncIcon,
  Search as SearchIcon,
  Link as LinkIcon,
  People as PeopleIcon,
  DeleteSweep as DeleteSweepIcon,
  DirectionsBus as RoutesIcon,
  AddCircleOutline as AddCrewIcon
} from '@material-ui/icons';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
    backgroundColor: '#f5f5f5',
    minHeight: '100vh'
  },
  header: {
    background: 'linear-gradient(45deg, #516e76 30%, #accacd 90%)',
    color: 'white',
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
    marginBottom: theme.spacing(3),
    boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)'
  },
  headerTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1)
  },
  statsContainer: {
    marginBottom: theme.spacing(3)
  },
  statCard: {
    height: '100%',
    transition: 'transform 0.2s, box-shadow 0.2s',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[8]
    }
  },
  statNumber: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main
  },
  statLabel: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1)
  },
  syncSection: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    backgroundColor: 'white'
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    boxShadow: theme.shadows[2],
    borderRadius: theme.spacing(1)
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    '& .MuiTableCell-head': {
      color: theme.palette.common.white,
      fontWeight: 600,
      fontSize: '0.875rem',
      borderBottom: 'none'
    }
  },
  tableRow: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  tabPanel: {
    paddingTop: theme.spacing(2)
  }
}));

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`sync-tabpanel-${index}`}
      aria-labelledby={`sync-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

function getInitialSyncTarget() {
  if (typeof window === 'undefined') return { tabIndex: 0, routeSearch: '' };
  const params = new URLSearchParams(window.location.search || '');
  const tab = String(params.get('tab') || '').toLowerCase();
  const routeSearch = params.get('route') || params.get('routeId') || '';
  return {
    tabIndex: tab === 'routes' || tab === 'route-crew' ? 4 : 0,
    routeSearch
  };
}

const AspireSyncDashboard = ({ auth }) => {
  const classes = useStyles();
  const [initialSyncTarget] = useState(() => getInitialSyncTarget());
  const [currentTab, setCurrentTab] = useState(initialSyncTarget.tabIndex);

  // Loading states
  const [propertyLoading, setPropertyLoading] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [employeeLoading, setEmployeeLoading] = useState(false);

  // Stats
  const [propertyStats, setPropertyStats] = useState(null);
  const [contactStats, setContactStats] = useState(null);
  const [companyStats, setCompanyStats] = useState(null);
  const [employeeStats, setEmployeeStats] = useState(null);

  // Matched/Unmatched data
  const [matchedProperties, setMatchedProperties] = useState([]);
  const [unmatchedProperties, setUnmatchedProperties] = useState([]);
  const [unmatchedPropertiesTotal, setUnmatchedPropertiesTotal] = useState(0);
  const [matchedContacts, setMatchedContacts] = useState([]);
  const [unmatchedContacts, setUnmatchedContacts] = useState([]);
  const [unmatchedContactsTotal, setUnmatchedContactsTotal] = useState(0);
  const [matchedCompanies, setMatchedCompanies] = useState([]);
  const [unmatchedCompanies, setUnmatchedCompanies] = useState([]);
  const [unmatchedCompaniesTotal, setUnmatchedCompaniesTotal] = useState(0);
  const [unmatchedEmployees, setUnmatchedEmployees] = useState([]);
  const [unmatchedEmployeesTotal, setUnmatchedEmployeesTotal] = useState(0);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Search filters for each tab
  const [companiesSearchQuery, setCompaniesSearchQuery] = useState('');
  const [propertiesSearchQuery, setPropertiesSearchQuery] = useState('');
  const [contactsSearchQuery, setContactsSearchQuery] = useState('');

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Match Dialog
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Conflict Dialog
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflictData, setConflictData] = useState(null);

  // Details Dialog
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [detailsType, setDetailsType] = useState(null); // 'contacts' or 'properties'
  const [detailsData, setDetailsData] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedCompanyForDetails, setSelectedCompanyForDetails] = useState(null);

  // Property Match Dialog
  const [propertyMatchDialogOpen, setPropertyMatchDialogOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [jobsiteSearchQuery, setJobsiteSearchQuery] = useState('');
  const [jobsiteSearchResults, setJobsiteSearchResults] = useState([]);
  const [jobsiteSearchLoading, setJobsiteSearchLoading] = useState(false);

  // Contact Match Dialog
  const [contactMatchDialogOpen, setContactMatchDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactCustomerSearchQuery, setContactCustomerSearchQuery] = useState('');
  const [contactCustomerSearchResults, setContactCustomerSearchResults] = useState([]);
  const [contactCustomerSearchLoading, setContactCustomerSearchLoading] = useState(false);

  // Route/Crew Matching
  const [routeStats, setRouteStats] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [unmatchedRoutes, setUnmatchedRoutes] = useState([]);
  const [unmatchedRoutesTotal, setUnmatchedRoutesTotal] = useState(0);
  const [routeMatchDialogOpen, setRouteMatchDialogOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [crewSearchQuery, setCrewSearchQuery] = useState('');
  const [crewSearchResults, setCrewSearchResults] = useState([]);
  const [crewSearchLoading, setCrewSearchLoading] = useState(false);
  const [routesSearchQuery, setRoutesSearchQuery] = useState(initialSyncTarget.routeSearch);

  // Create Crew from Route Dialog
  const [createCrewDialogOpen, setCreateCrewDialogOpen] = useState(false);
  const [createCrewRoute, setCreateCrewRoute] = useState(null);
  const [createCrewData, setCreateCrewData] = useState({ crewName: '', division: '', foremanName: '' });

  // Reset/Cleanup Dialog
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [cleanupStatus, setCleanupStatus] = useState(null);

  // Background job tracking
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobProgress, setJobProgress] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);

  const sleep = (ms = 1000) => new Promise(resolve => setTimeout(resolve, ms));

  const waitForJobCompletion = async (jobId, label = 'Job') => {
    const timeoutMs = 10 * 60 * 1000; // 10 minutes
    const start = Date.now();

    while (true) {
      const response = await axios.get(`/api/aspire/jobs/${jobId}`);

      if (!response.data.success) {
        throw new Error(`${label} status check failed`);
      }

      const job = response.data.job;
      console.log(`[Job ${jobId}] Status: ${job.status}, Progress: ${job.progress?.percentage ?? 0}% - ${job.progress?.message || ''}`);

      if (job.status === 'completed') {
        return job;
      }

      if (job.status === 'failed') {
        throw new Error(job.error || `${label} failed`);
      }

      if (Date.now() - start > timeoutMs) {
        throw new Error(`${label} timed out after ${(timeoutMs / 60000).toFixed(1)} minutes`);
      }

      await sleep(2000);
    }
  };

  const syncPropertiesBlocking = async () => {
    try {
      setPropertyLoading(true);
      showSnackbar('Starting property sync (waiting for completion)...', 'info');

      const response = await axios.get('/api/aspire/properties/sync', {
        params: {
          branchIds: [4, 5],
          autoUpdate: true,
          generateReport: false,
          maxGeoDistance: 50,
          minAddressScore: 0.8,
          minNameScore: 0.6,
          async: true
        }
      });

      if (response.data.success && response.data.async) {
        const job = await waitForJobCompletion(response.data.jobId, 'Property sync');
        const jobSummary = job.result?.summary || {};
        showSnackbar(
          `Properties synced: ${jobSummary.matched || 0} matched, ${job.result?.unmatchedCount || 0} unmatched`,
          'success'
        );
        await loadPropertyStatus();
        return job;
      } else if (response.data.success) {
        showSnackbar(
          `Properties synced: ${response.data.summary.matched} matched, ${response.data.unmatchedCount} unmatched`,
          'success'
        );
        await loadPropertyStatus();
        return response.data;
      } else {
        throw new Error(response.data.message || 'Property sync failed');
      }
    } catch (error) {
      console.error('Error syncing properties (blocking):', error);
      showSnackbar(`Property sync failed: ${error.message}`, 'error');
      throw error;
    } finally {
      setPropertyLoading(false);
      setJobProgress(null);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Open match dialog
  const openMatchDialog = (company) => {
    setSelectedCompany(company);
    setMatchDialogOpen(true);
    setCustomerSearchQuery('');
    setCustomerSearchResults([]);
  };

  // Close match dialog
  const closeMatchDialog = () => {
    setMatchDialogOpen(false);
    setSelectedCompany(null);
    setCustomerSearchQuery('');
    setCustomerSearchResults([]);
  };

  // Search customers
  const searchCustomers = async (query) => {
    if (!query || query.trim().length < 2) {
      setCustomerSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      const response = await axios.get('/api/aspire/companies/search-customers', {
        params: { query, limit: 20 }
      });

      if (response.data.success) {
        setCustomerSearchResults(response.data.customers);
      }
    } catch (error) {
      console.error('Error searching customers:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  // Handle customer selection for manual match
  const handleManualMatch = async (customer, force = false) => {
    if (!selectedCompany) return;

    try {
      const response = await axios.post('/api/aspire/companies/update-match', {
        companyId: selectedCompany.CompanyID,
        customerId: customer._id,
        confidence: 1.0,
        force
      });

      if (response.data.success) {
        showSnackbar(
          `Successfully matched ${selectedCompany.CompanyName} to ${customer.displayName}!`,
          'success'
        );
        closeMatchDialog();
        closeConflictDialog();
        await loadCompanyStatus();
        await loadUnmatchedCompanies();
      }
    } catch (error) {
      // Check if this is a conflict (409 status)
      if (error.response?.status === 409 && error.response?.data?.conflict) {
        // Show conflict resolution dialog
        setConflictData({
          existingMatch: error.response.data.existingMatch,
          proposedMatch: error.response.data.proposedMatch,
          selectedCustomer: customer
        });
        setConflictDialogOpen(true);
      } else {
        showSnackbar(
          error.response?.data?.error || 'Failed to match company',
          'error'
        );
      }
    }
  };

  // Handle conflict resolution - user confirms they want to replace the match
  const handleConfirmReplaceMatch = async () => {
    if (!conflictData || !selectedCompany) return;

    // Retry the match with force=true
    await handleManualMatch(conflictData.selectedCustomer, true);
  };

  // Close conflict dialog
  const closeConflictDialog = () => {
    setConflictDialogOpen(false);
    setConflictData(null);
  };

  // Handle search input change
  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setCustomerSearchQuery(query);
    searchCustomers(query);
  };

  // Open details dialog for contacts or properties
  const openDetailsDialog = async (company, type) => {
    setSelectedCompanyForDetails(company);
    setDetailsType(type);
    setDetailsDialogOpen(true);
    setDetailsLoading(true);
    setDetailsData([]);

    try {
      const endpoint = type === 'contacts'
        ? `/api/aspire/companies/${company.CompanyID}/contacts`
        : `/api/aspire/companies/${company.CompanyID}/properties`;

      const response = await axios.get(endpoint);

      if (response.data.success) {
        setDetailsData(response.data[type] || []);
      }
    } catch (error) {
      console.error(`Error loading ${type}:`, error);
      showSnackbar(`Failed to load ${type}`, 'error');
    } finally {
      setDetailsLoading(false);
    }
  };

  // Close details dialog
  const closeDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setDetailsType(null);
    setDetailsData([]);
    setSelectedCompanyForDetails(null);
  };

  // Load Property Status
  const loadPropertyStatus = useCallback(async () => {
    try {
      const response = await axios.get('/api/aspire/properties/status');
      if (response.data.success) {
        setPropertyStats(response.data.status);
      }
    } catch (error) {
      console.error('Error loading property status:', error);
    }
  }, []);

  // Load Contact Status
  const loadContactStatus = useCallback(async () => {
    try {
      const response = await axios.get('/api/aspire/contacts/status');
      if (response.data.success) {
        setContactStats(response.data.status);
      }
    } catch (error) {
      console.error('Error loading contact status:', error);
    }
  }, []);

  // Load Unmatched Contacts
  const loadUnmatchedContacts = useCallback(async () => {
    try {
      const response = await axios.get('/api/aspire/contacts/unmatched', {
        params: {
          limit: rowsPerPage,
          page: page
        }
      });
      if (response.data.success) {
        setUnmatchedContacts(response.data.contacts);
        setUnmatchedContactsTotal(response.data.total);
      }
    } catch (error) {
      console.error('Error loading unmatched contacts:', error);
    }
  }, [page, rowsPerPage]);

  // Load Unmatched Companies
  const loadUnmatchedCompanies = useCallback(async () => {
    try {
      const response = await axios.get('/api/aspire/companies/unmatched', {
        params: {
          limit: rowsPerPage,
          page: page,
          activeOnly: true
        }
      });
      if (response.data.success) {
        setUnmatchedCompanies(response.data.companies);
        setUnmatchedCompaniesTotal(response.data.total);
      }
    } catch (error) {
      console.error('Error loading unmatched companies:', error);
    }
  }, [page, rowsPerPage]);

  // Load Unmatched Properties
  const loadUnmatchedProperties = useCallback(async () => {
    try {
      const response = await axios.get('/api/aspire/properties/unmatched', {
        params: {
          limit: rowsPerPage,
          skip: page * rowsPerPage
        }
      });
      if (response.data.success) {
        // API returns unmatchedProperties array of objects with {property, reason}
        const properties = response.data.unmatchedProperties || [];

        // Extract just the property objects and apply pagination manually
        const allProperties = properties.map(item => item.property);
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        const paginatedProperties = allProperties.slice(startIndex, endIndex);

        setUnmatchedProperties(paginatedProperties);
        setUnmatchedPropertiesTotal(allProperties.length);
      }
    } catch (error) {
      console.error('Error loading unmatched properties:', error);
      setUnmatchedProperties([]);
      setUnmatchedPropertiesTotal(0);
    }
  }, [page, rowsPerPage]);

  // Load Employee Stats
  const loadEmployeeStats = useCallback(async () => {
    try {
      const response = await axios.get('/api/aspire/contacts/employee-stats');
      if (response.data.success) {
        setEmployeeStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error loading employee stats:', error);
    }
  }, []);

  // Load Unmatched Employees
  const loadUnmatchedEmployees = useCallback(async () => {
    try {
      const response = await axios.get('/api/aspire/contacts/unmatched-employees', {
        params: {
          limit: rowsPerPage,
          page: page
        }
      });
      if (response.data.success) {
        setUnmatchedEmployees(response.data.contacts);
        setUnmatchedEmployeesTotal(response.data.total);
      }
    } catch (error) {
      console.error('Error loading unmatched employees:', error);
    }
  }, [page, rowsPerPage]);

  // Load Route Status
  const loadRouteStatus = useCallback(async () => {
    try {
      const response = await axios.get('/api/aspire/routes/status');
      if (response.data.success) {
        setRouteStats(response.data.status);
      }
    } catch (error) {
      console.error('Error loading route status:', error);
    }
  }, []);

  // Load Unmatched Routes
  const loadUnmatchedRoutes = useCallback(async () => {
    try {
      const response = await axios.get('/api/aspire/routes/unmatched', {
        params: {
          limit: rowsPerPage,
          page,
          search: routesSearchQuery.trim() || undefined
        }
      });
      if (response.data.success) {
        setUnmatchedRoutes(response.data.routes);
        setUnmatchedRoutesTotal(response.data.total);
      }
    } catch (error) {
      console.error('Error loading unmatched routes:', error);
    }
  }, [page, rowsPerPage, routesSearchQuery]);

  // Search crews for route matching
  const searchCrews = async (query) => {
    if (!query || query.length < 1) {
      setCrewSearchResults([]);
      return;
    }
    try {
      setCrewSearchLoading(true);
      const response = await axios.get('/api/aspire/routes/search-crews', {
        params: { query, limit: 20 }
      });
      setCrewSearchResults(response.data.crews || []);
    } catch (error) {
      console.error('Error searching crews:', error);
      showSnackbar('Failed to search crews', 'error');
    } finally {
      setCrewSearchLoading(false);
    }
  };

  // Open Route Match Dialog
  const openRouteMatchDialog = (route) => {
    setSelectedRoute(route);
    setRouteMatchDialogOpen(true);
    setCrewSearchQuery('');
    setCrewSearchResults([]);
  };

  // Match route to crew
  const handleRouteMatch = async (crew) => {
    try {
      const response = await axios.post('/api/aspire/routes/update-match', {
        routeId: selectedRoute.RouteID,
        crewId: crew._id
      });

      if (response.data.success) {
        showSnackbar(response.data.message, 'success');
        setRouteMatchDialogOpen(false);
        await loadUnmatchedRoutes();
        await loadRouteStatus();
      }
    } catch (error) {
      console.error('Error matching route:', error);
      showSnackbar(`Match failed: ${error.response?.data?.error || error.message}`, 'error');
    }
  };

  // Auto-match routes to crews
  const autoMatchRoutes = async () => {
    try {
      setRouteLoading(true);
      showSnackbar('Auto-matching routes to crews...', 'info');

      const response = await axios.post('/api/aspire/routes/auto-match');

      if (response.data.success) {
        showSnackbar(
          `Auto-match complete: ${response.data.matched} of ${response.data.total} routes matched, ${response.data.unmatched} remaining`,
          response.data.matched > 0 ? 'success' : 'info'
        );
        await loadRouteStatus();
        await loadUnmatchedRoutes();
      }
    } catch (error) {
      console.error('Error auto-matching routes:', error);
      showSnackbar(`Auto-match failed: ${error.message}`, 'error');
    } finally {
      setRouteLoading(false);
    }
  };

  // Open the create crew dialog pre-filled with route info
  const handleCreateCrewFromRoute = (route) => {
    const defaultName = route.RouteName?.match(/(#\d+)/)?.[1] || route.RouteName || '';
    setCreateCrewRoute(route);
    setCreateCrewData({
      crewName: defaultName,
      division: route.DivisionName || '',
      foremanName: route.CrewLeaderName || ''
    });
    setCreateCrewDialogOpen(true);
  };

  // Submit crew creation from the dialog
  const handleSubmitCreateCrew = async () => {
    if (!createCrewRoute) return;

    try {
      setRouteLoading(true);
      const response = await axios.post('/api/aspire/routes/create-crew', {
        routeId: createCrewRoute.RouteID,
        crewName: createCrewData.crewName || undefined,
        division: createCrewData.division || undefined,
        foremanName: createCrewData.foremanName || undefined
      });

      if (response.data.success) {
        showSnackbar(response.data.message, 'success');
        setCreateCrewDialogOpen(false);
        setRouteMatchDialogOpen(false);
        await loadUnmatchedRoutes();
        await loadRouteStatus();
      }
    } catch (error) {
      console.error('Error creating crew from route:', error);
      showSnackbar(
        error.response?.data?.error || `Failed to create crew: ${error.message}`,
        'error'
      );
    } finally {
      setRouteLoading(false);
    }
  };

  // Open Property Match Dialog
  const openPropertyMatchDialog = (property) => {
    setSelectedProperty(property);
    setPropertyMatchDialogOpen(true);
    setJobsiteSearchQuery('');
    setJobsiteSearchResults([]);
  };

  // Search for jobsites
  const searchJobsites = async (query) => {
    if (!query || query.length < 2) {
      setJobsiteSearchResults([]);
      return;
    }

    try {
      setJobsiteSearchLoading(true);
      const response = await axios.get('/api/aspire/properties/jobsites', {
        params: { search: query, limit: 10 }
      });
      setJobsiteSearchResults(response.data.jobsites || []);
    } catch (error) {
      console.error('Error searching jobsites:', error);
      showSnackbar('Failed to search jobsites', 'error');
    } finally {
      setJobsiteSearchLoading(false);
    }
  };

  // Manually match property to jobsite
  const handlePropertyMatch = async (jobsite) => {
    try {
      // Warn if jobsite is already matched to another property
      if (jobsite.aspirePropertyId && jobsite.aspirePropertyId !== selectedProperty.PropertyID) {
        const confirmed = window.confirm(
          `WARNING: This jobsite is already matched to Aspire Property ${jobsite.aspirePropertyId}.\n\n` +
          `Matching Property ${selectedProperty.PropertyID} to this jobsite will OVERRIDE the existing match.\n\n` +
          `Do you want to continue?`
        );
        if (!confirmed) {
          return;
        }
      }

      const response = await axios.post('/api/aspire/properties/update-match', {
        propertyId: selectedProperty.PropertyID,
        jobsiteId: jobsite._id,
        confidence: 1.0
      });

      if (response.data.success) {
        showSnackbar(`Property ${selectedProperty.PropertyID} matched to jobsite "${jobsite.name}"`, 'success');
        setPropertyMatchDialogOpen(false);
        await loadUnmatchedProperties();
        await loadPropertyStatus();
      }
    } catch (error) {
      console.error('Error matching property:', error);
      showSnackbar(`Match failed: ${error.message}`, 'error');
    }
  };

  // Open Contact Match Dialog
  const openContactMatchDialog = (contact) => {
    setSelectedContact(contact);
    setContactMatchDialogOpen(true);
    setContactCustomerSearchQuery('');
    setContactCustomerSearchResults([]);
  };

  // Search for customers (for contact matching)
  const searchCustomersForContact = async (query) => {
    if (!query || query.length < 2) {
      setContactCustomerSearchResults([]);
      return;
    }

    try {
      setContactCustomerSearchLoading(true);
      const response = await axios.get('/api/aspire/contacts/search-customers', {
        params: { query, limit: 20 }
      });
      setContactCustomerSearchResults(response.data.customers || []);
    } catch (error) {
      console.error('Error searching customers:', error);
      showSnackbar('Failed to search customers', 'error');
    } finally {
      setContactCustomerSearchLoading(false);
    }
  };

  // Manually match contact to customer
  const handleContactMatch = async (customer) => {
    try {
      // Warn if customer is already matched to another contact
      if (customer.aspireContactId && customer.aspireContactId !== selectedContact.ContactID) {
        const confirmed = window.confirm(
          `WARNING: This customer is already matched to Aspire Contact ${customer.aspireContactId}.\n\n` +
          `Matching Contact ${selectedContact.ContactID} to this customer will OVERRIDE the existing match.\n\n` +
          `Do you want to continue?`
        );
        if (!confirmed) {
          return;
        }
      }

      const response = await axios.post('/api/aspire/contacts/update-match', {
        contactId: selectedContact.ContactID,
        customerId: customer._id,
        confidence: 1.0
      });

      if (response.data.success) {
        const customerName = customer.displayName || customer.companyName || customer.email;
        showSnackbar(`Contact ${selectedContact.ContactID} matched to customer "${customerName}"`, 'success');
        setContactMatchDialogOpen(false);
        await loadUnmatchedContacts();
        await loadContactStatus();
      }
    } catch (error) {
      console.error('Error matching contact:', error);
      showSnackbar(`Match failed: ${error.message}`, 'error');
    }
  };

  // Load Company Status
  const loadCompanyStatus = useCallback(async () => {
    try {
      const response = await axios.get('/api/aspire/companies/status');
      if (response.data.success) {
        setCompanyStats(response.data.status);
      }
    } catch (error) {
      console.error('Error loading company status:', error);
    }
  }, []);

  // Poll job status
  const pollJobStatus = async (jobId) => {
    try {
      const response = await axios.get(`/api/aspire/jobs/${jobId}`);

      if (response.data.success) {
        const job = response.data.job;
        setJobProgress(job.progress);

        console.log(`[Job ${jobId}] Status: ${job.status}, Progress: ${job.progress.percentage}% - ${job.progress.message}`);

        if (job.status === 'completed') {
          // Job finished successfully
          clearInterval(pollingInterval);
          setPollingInterval(null);
          setCurrentJobId(null);
          setPropertyLoading(false);

          showSnackbar(
            `Properties synced: ${job.result.summary.matched} matched, ${job.result.unmatchedCount} unmatched`,
            'success'
          );

          // Backfill CompanyID on properties (links properties to companies)
          showSnackbar('Linking properties to companies...', 'info');
          try {
            await axios.post('/api/aspire/properties/backfill-company-id');
            console.log('Property-Company linking completed');
          } catch (backfillError) {
            console.error('Backfill error (non-critical):', backfillError);
          }

          await loadPropertyStatus();
          setJobProgress(null);
        } else if (job.status === 'failed') {
          // Job failed
          clearInterval(pollingInterval);
          setPollingInterval(null);
          setCurrentJobId(null);
          setPropertyLoading(false);
          setJobProgress(null);
          showSnackbar(`Property sync failed: ${job.error}`, 'error');
        }
      }
    } catch (error) {
      console.error('Error polling job status:', error);
      // Don't stop polling on error, might be temporary
    }
  };

  // Sync Properties (with optional background job)
  const syncProperties = async (options = {}) => {
    const { runAsync = true } = options || {};

    try {
      setPropertyLoading(true);
      showSnackbar('Starting property sync...', 'info');

      const response = await axios.get('/api/aspire/properties/sync', {
        params: {
          branchIds: [4, 5],
          autoUpdate: true,
          generateReport: false,
          maxGeoDistance: 50,
          minAddressScore: 0.8,
          minNameScore: 0.6,
          async: runAsync
        }
      });

      if (runAsync && response.data.success && response.data.async) {
        // Job started in background
        const jobId = response.data.jobId;
        setCurrentJobId(jobId);
        setJobProgress({ current: 0, total: 100, percentage: 0, message: 'Initializing...' });

        showSnackbar('Property sync running in background...', 'info');
        console.log(`Property sync job started: ${jobId}`);

        // Start polling for status
        const interval = setInterval(() => {
          pollJobStatus(jobId);
        }, 2000); // Poll every 2 seconds

        setPollingInterval(interval);

        // Do initial poll immediately
        pollJobStatus(jobId);
      } else if (response.data.success) {
        // Synchronous mode (used by Sync All flow)
        showSnackbar(
          `Properties synced: ${response.data.summary.matched} matched, ${response.data.unmatchedCount} unmatched`,
          'success'
        );
        await loadPropertyStatus();
        setPropertyLoading(false);
      } else {
        setPropertyLoading(false);
      }
    } catch (error) {
      console.error('Error syncing properties:', error);
      showSnackbar(`Property sync failed: ${error.message}`, 'error');
      setPropertyLoading(false);
      setJobProgress(null);
    }
  };

  // Backfill Property Visit Dates
  const backfillVisitDates = async () => {
    try {
      setPropertyLoading(true);
      showSnackbar('Starting property visit date backfill (this may take a minute)...', 'info');

      const response = await axios.post('/api/aspire/routing/backfill-visit-dates');

      if (response.data.success) {
        showSnackbar(
          `Backfill complete: ${response.data.results.updated} properties updated`,
          'success'
        );
        await loadPropertyStatus();
      }
    } catch (error) {
      console.error('Error backfilling visit dates:', error);
      showSnackbar(`Backfill failed: ${error.message}`, 'error');
    } finally {
      setPropertyLoading(false);
    }
  };

  // Sync Contacts
  const syncContacts = async () => {
    try {
      setContactLoading(true);
      showSnackbar('Syncing contacts from Aspire...', 'info');

      const response = await axios.get('/api/aspire/contacts/sync', {
        params: {
          branchIds: [4, 5]
        }
      });

      if (response.data.success) {
        showSnackbar(
          `Contacts synced: ${response.data.summary.totalContacts} total`,
          'success'
        );
        await loadContactStatus();
      }
    } catch (error) {
      console.error('Error syncing contacts:', error);
      showSnackbar(`Contact sync failed: ${error.message}`, 'error');
    } finally {
      setContactLoading(false);
    }
  };

  // Match Contacts (auto-match via email/name)
  const matchContacts = async () => {
    try {
      setContactLoading(true);
      showSnackbar('Matching contacts to Portal contacts via email/name...', 'info');

      const response = await axios.post('/api/aspire/contacts/auto-match', {
        minConfidence: 0.9,
        autoUpdate: true,
        async: true // Run as background job to avoid nginx timeout
      });

      if (response.data.success && response.data.jobId) {
        // Poll for job completion
        const job = await waitForJobCompletion(response.data.jobId, 'Contact Matching');

        if (job.result) {
          const { exactEmailMatches = 0, exactNameMatches = 0, companiesAutoMatched = 0 } = job.result.summary || {};
          const contactMsg = `Contacts matched: ${job.result.matches || 0} total (${exactEmailMatches} by email, ${exactNameMatches} by name)`;
          const companyMsg = companiesAutoMatched > 0 ? ` | ${companiesAutoMatched} companies auto-matched via consensus!` : '';
          showSnackbar(
            contactMsg + companyMsg,
            'success'
          );
        }

        await loadContactStatus();
        await loadCompanyStatus(); // Refresh company stats since we auto-matched some
        if (currentTab === 1) {
          await loadUnmatchedContacts();
        }
        if (currentTab === 2) {
          await loadUnmatchedCompanies();
        }
      }
    } catch (error) {
      console.error('Error matching contacts:', error);
      showSnackbar(`Contact matching failed: ${error.message}`, 'error');
    } finally {
      setContactLoading(false);
    }
  };

  // Sync Companies
  const syncCompanies = async () => {
    try {
      setCompanyLoading(true);
      showSnackbar('Syncing companies from Aspire (filtered by Branch 4 & 5 contacts)...', 'info');

      const response = await axios.get('/api/aspire/companies/sync', {
        params: {
          branchIds: [4, 5],
          activeOnly: false
        }
      });

      if (response.data.success) {
        const msg = response.data.message
          ? `${response.data.message}`
          : `Companies synced: ${response.data.summary.totalCompanies} total (filtered by branches 4 & 5)`;
        showSnackbar(msg, 'success');
        await loadCompanyStatus();
      }
    } catch (error) {
      console.error('Error syncing companies:', error);
      showSnackbar(`Company sync failed: ${error.message}`, 'error');
    } finally {
      setCompanyLoading(false);
    }
  };

  // Match Companies (async with job polling to avoid nginx timeout)
  const matchCompanies = async () => {
    try {
      setCompanyLoading(true);
      showSnackbar('Matching companies to customers via contacts...', 'info');

      const response = await axios.post('/api/aspire/companies/match', {
        minContactCount: 1,
        minConsensusPercent: 0.8,
        autoUpdate: true,
        async: true // Run as background job
      });

      if (response.data.success && response.data.jobId) {
        // Poll for job completion
        const job = await waitForJobCompletion(response.data.jobId, 'Company Matching');

        if (job.result) {
          const { nameMatches = 0, contactMatches = 0, strongMatches = 0 } = job.result.summary || {};
          const matchCount = job.result.matches || strongMatches || 0;
          showSnackbar(
            `Companies matched: ${matchCount} total (${nameMatches} by name, ${contactMatches} by contacts)`,
            'success'
          );
        }

        await loadCompanyStatus();
        if (currentTab === 2) {
          await loadUnmatchedCompanies();
        }
      }
    } catch (error) {
      console.error('Error matching companies:', error);
      showSnackbar(`Company matching failed: ${error.message}`, 'error');
    } finally {
      setCompanyLoading(false);
    }
  };

  // Backfill Company Matches
  const backfillCompanyMatches = async () => {
    try {
      showSnackbar('Auto-linking companies via matched contacts/properties...', 'info');
      const response = await axios.post('/api/aspire/companies/backfill-matches');

      if (response.data.success) {
        showSnackbar(response.data.message, 'success');
        await loadCompanyStatus();
        if (currentTab === 2) {
          await loadUnmatchedCompanies();
        }
      }
    } catch (error) {
      console.error('Error backfilling company matches:', error);
      showSnackbar(`Backfill failed: ${error.message}`, 'error');
    }
  };

  // Sync All
  const syncAll = async () => {
    showSnackbar('Starting complete sync...', 'info');

    // Step 1: Sync Contacts
    try {
      await syncContacts();
      console.log('✅ Step 1: Contacts synced');
    } catch (error) {
      console.error('❌ Step 1 failed:', error);
      showSnackbar('Contact sync failed, continuing...', 'warning');
    }

    // Step 2: Sync Properties
    try {
      await syncPropertiesBlocking();
      console.log('✅ Step 2: Properties synced');
    } catch (error) {
      console.error('❌ Step 2 failed:', error);
      showSnackbar('Property sync failed, continuing...', 'warning');
    }

    // Step 2.5: Extract and sync property contacts
    showSnackbar('Extracting contacts from properties...', 'info');
    try {
      const response = await axios.post('/api/aspire/contacts/sync-property-contacts');
      if (response.data.success) {
        const { stats } = response.data;
        console.log('✅ Step 2.5: Property contacts synced:', stats);
        showSnackbar(
          `Property contacts synced: ${stats.newContactsFetched} new, ${stats.alreadySynced} existing`,
          'success'
        );
      }
    } catch (error) {
      console.error('❌ Step 2.5 failed:', error);
      showSnackbar('Property contact sync failed, continuing...', 'warning');
    }

    // Step 3: Match Contacts (CRITICAL - this enables company matching!)
    try {
      await matchContacts();
      console.log('✅ Step 3: Contacts matched');
    } catch (error) {
      console.error('❌ Step 3 failed:', error);
      showSnackbar('Contact matching failed, continuing...', 'warning');
    }

    // Step 4: Sync Companies
    try {
      await syncCompanies();
      console.log('✅ Step 4: Companies synced');
    } catch (error) {
      console.error('❌ Step 4 failed:', error);
      showSnackbar('Company sync failed, continuing...', 'warning');
    }

    // Step 5: Match Companies
    try {
      await matchCompanies();
      console.log('✅ Step 5: Companies matched');
    } catch (error) {
      console.error('❌ Step 5 failed:', error);
      showSnackbar('Company matching failed, continuing...', 'warning');
    }

    // Step 6: Smart-link companies via matched contacts/properties
    showSnackbar('Auto-linking companies via matched contacts/properties...', 'info');
    try {
      const response = await axios.post('/api/aspire/companies/backfill-matches');
      if (response.data.success) {
        console.log('✅ Step 6: Company backfill completed:', response.data.stats);
      }
    } catch (backfillError) {
      console.error('❌ Step 6 failed (non-critical):', backfillError);
    }

    // Step 7: Backfill Property-Company links (uses async job with polling)
    showSnackbar('Linking properties to companies via customers...', 'info');
    try {
      const response = await axios.post('/api/aspire/properties/backfill-company-id', { async: true });

      if (response.data.success && response.data.jobId) {
        // Poll for job completion
        const job = await waitForJobCompletion(response.data.jobId, 'Property-Company Backfill');
        console.log('✅ Step 7: Property-Company linking completed');
      }
    } catch (backfillError) {
      console.error('❌ Step 7 failed:', backfillError);
      showSnackbar('Property-Company linking failed, continuing...', 'warning');
    }

    // Wait 3 seconds for database writes to propagate
    showSnackbar('Finalizing data consistency...', 'info');
    await new Promise(resolve => setTimeout(resolve, 3000));

    // Step 8: Generate unmatched workbook (uses async job with polling)
    showSnackbar('Generating unmatched creation workbook...', 'info');
    try {
      const reportResponse = await axios.post('/api/aspire/companies/unmatched-report', { async: true });

      if (reportResponse.data.success && reportResponse.data.jobId) {
        // Poll for job completion
        const job = await waitForJobCompletion(reportResponse.data.jobId, 'Report Generation');

        if (job.result && job.result.report) {
          console.log('✅ Step 8: Unmatched workbook generated:', job.result.report);
          showSnackbar(`Unmatched workbook ready: ${job.result.report.filename}`, 'success');
        }
      }
    } catch (reportError) {
      console.error('❌ Step 8 failed:', reportError);
      showSnackbar('Failed to generate unmatched workbook. Check console for details.', 'error');
    }

    showSnackbar('Complete sync finished! Check console for details.', 'success');
    await loadCompanyStatus(); // Refresh stats to show newly matched companies
  };

  // Auto-Create Entities
  const autoCreateEntities = async () => {
    try {
      showSnackbar('Starting auto-creation of entities from Aspire data...', 'info');

      const response = await axios.post('/api/aspire/companies/auto-create-entities');

      if (response.data.success) {
        const { stats, workbookReport } = response.data;

        // Build success message
        const message = `Auto-creation complete! Created ${stats.customers.created} customers, ${stats.contacts.created} contacts, ${stats.employees.created} employees, ${stats.jobsites.created} jobsites. Skipped ${stats.customers.skipped + stats.contacts.skipped + stats.employees.skipped + stats.jobsites.skipped} records.`;

        showSnackbar(message, 'success');

        if (workbookReport) {
          showSnackbar(`Unable to create workbook generated: ${workbookReport.filename}`, 'info');
        }

        console.log('✅ Auto-creation complete:', response.data);

        // Refresh all statuses
        await loadPropertyStatus();
        await loadContactStatus();
        await loadCompanyStatus();
        await loadEmployeeStats();

        // Reload unmatched lists based on current tab
        if (currentTab === 0) {
          await loadUnmatchedProperties();
        } else if (currentTab === 1) {
          await loadUnmatchedContacts();
        } else if (currentTab === 2) {
          await loadUnmatchedCompanies();
        } else if (currentTab === 3) {
          await loadUnmatchedEmployees();
        } else if (currentTab === 4) {
          await loadUnmatchedRoutes();
        }
      }
    } catch (error) {
      console.error('Error auto-creating entities:', error);
      showSnackbar(`Auto-creation failed: ${error.message}`, 'error');
    }
  };

  // Cleanup Unrelated Companies
  const cleanupUnrelatedCompanies = async () => {
    try {
      showSnackbar('Removing companies not associated with branches 4 & 5...', 'info');

      const response = await axios.post('/api/aspire/companies/cleanup-unrelated');

      if (response.data.success) {
        const { stats } = response.data;

        const message = `Cleanup complete! Deleted ${stats.deleted} companies. ${stats.remaining} companies remaining (associated with branches 4 & 5).`;

        showSnackbar(message, 'success');

        console.log('✅ Company cleanup complete:', response.data);

        // Refresh company status
        await loadCompanyStatus();

        // Reload unmatched companies if on that tab
        if (currentTab === 2) {
          await loadUnmatchedCompanies();
        }
      }
    } catch (error) {
      console.error('Error cleaning up companies:', error);
      showSnackbar(`Cleanup failed: ${error.message}`, 'error');
    }
  };

  // Open Reset Dialog
  const openResetDialog = async () => {
    try {
      // Fetch cleanup status to show user what will be deleted
      const response = await axios.get('/api/aspire/cleanup/status');
      if (response.data.success) {
        setCleanupStatus(response.data.status);
        setResetDialogOpen(true);
      }
    } catch (error) {
      console.error('Error fetching cleanup status:', error);
      showSnackbar('Failed to fetch cleanup status', 'error');
    }
  };

  // Reset All Aspire Data
  const handleResetAll = async () => {
    try {
      setResetLoading(true);
      showSnackbar('Resetting all Aspire data...', 'info');

      const response = await axios.post('/api/aspire/cleanup/reset-all');

      if (response.data.success) {
        const { results } = response.data;
        const autoCreated = results.autoCreatedEntities || {};
        const totalAutoCreated = (autoCreated.customers || 0) + (autoCreated.contacts || 0) +
                                 (autoCreated.jobsites || 0) + (autoCreated.employees || 0);

        let message = `Reset complete! Deleted ${results.aspireCollections.companies} Aspire companies, ${results.aspireCollections.contacts} Aspire contacts, ${results.aspireCollections.properties} Aspire properties.`;

        if (totalAutoCreated > 0) {
          message += ` Also deleted ${autoCreated.customers} auto-created customers, ${autoCreated.contacts} auto-created contacts, ${autoCreated.jobsites} auto-created jobsites, ${autoCreated.employees} auto-created employees.`;
        }

        if (results.portalReferences.customers > 0 || results.portalReferences.contacts > 0 || results.portalReferences.jobsites > 0) {
          message += ` Cleared Aspire references from ${results.portalReferences.customers} existing customers, ${results.portalReferences.contacts} existing contacts, ${results.portalReferences.jobsites} existing jobsites.`;
        }

        showSnackbar(message, 'success');

        // Reload all stats
        await loadPropertyStatus();
        await loadContactStatus();
        await loadCompanyStatus();
        await loadEmployeeStats();

        setResetDialogOpen(false);
      }
    } catch (error) {
      console.error('Error resetting Aspire data:', error);
      showSnackbar(`Reset failed: ${error.message}`, 'error');
    } finally {
      setResetLoading(false);
    }
  };

  // Load all statuses on mount
  useEffect(() => {
    loadPropertyStatus();
    loadContactStatus();
    loadCompanyStatus();
    loadEmployeeStats();
    loadRouteStatus();
  }, [loadPropertyStatus, loadContactStatus, loadCompanyStatus, loadEmployeeStats, loadRouteStatus]);

  // Load unmatched properties when tab changes to Properties or pagination changes
  useEffect(() => {
    if (currentTab === 0) {
      loadUnmatchedProperties();
    }
  }, [currentTab, page, rowsPerPage, loadUnmatchedProperties]);

  // Load unmatched contacts when tab changes to Contacts or pagination changes
  useEffect(() => {
    if (currentTab === 1) {
      loadUnmatchedContacts();
    }
  }, [currentTab, page, rowsPerPage, loadUnmatchedContacts]);

  // Load unmatched companies when tab changes to Companies or pagination changes
  useEffect(() => {
    if (currentTab === 2) {
      loadUnmatchedCompanies();
    }
  }, [currentTab, page, rowsPerPage, loadUnmatchedCompanies]);

  // Load unmatched employees when tab changes to Employees or pagination changes
  useEffect(() => {
    if (currentTab === 3) {
      loadUnmatchedEmployees();
    }
  }, [currentTab, page, rowsPerPage, loadUnmatchedEmployees]);

  // Load unmatched routes when tab changes to Routes or pagination changes
  useEffect(() => {
    if (currentTab === 4) {
      loadUnmatchedRoutes();
    }
  }, [currentTab, page, rowsPerPage, loadUnmatchedRoutes]);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setPage(0);
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Container maxWidth="xl" className={classes.root}>
      {/* Header */}
      <Paper className={classes.header} elevation={3}>
        <Typography variant="h4" className={classes.headerTitle}>
          <SyncIcon style={{ verticalAlign: 'middle', marginRight: 12 }} />
          Aspire Sync Dashboard
        </Typography>
        <Typography variant="subtitle1" style={{ opacity: 0.95, marginBottom: 16 }}>
          Sync and match Properties, Contacts, and Companies between Aspire and Portal
        </Typography>

        {/* Quick Actions */}
        <Box display="flex" gap={2} mt={2}>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            startIcon={<SyncIcon />}
            onClick={syncAll}
            disabled={propertyLoading || contactLoading || companyLoading}
          >
            Sync Everything
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<CheckCircleIcon />}
            onClick={autoCreateEntities}
            disabled={propertyLoading || contactLoading || companyLoading}
          >
            Auto-Create Entities
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="large"
            startIcon={<DeleteSweepIcon />}
            onClick={cleanupUnrelatedCompanies}
            disabled={companyLoading}
          >
            Cleanup Companies
          </Button>
          <Button
            variant="outlined"
            size="large"
            style={{ color: '#ff5252', borderColor: '#ff5252' }}
            startIcon={<WarningIcon />}
            onClick={openResetDialog}
            disabled={resetLoading}
          >
            Reset All Data
          </Button>
          <Button
            variant="outlined"
            style={{ color: 'white', borderColor: 'white' }}
            startIcon={<RefreshIcon />}
            onClick={() => {
              loadPropertyStatus();
              loadContactStatus();
              loadCompanyStatus();
              loadEmployeeStats();
              loadRouteStatus();
            }}
          >
            Refresh Stats
          </Button>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper elevation={2}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab
            label={
              <Box>
                <HomeIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                Properties
              </Box>
            }
          />
          <Tab
            label={
              <Box>
                <ContactsIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                Contacts
              </Box>
            }
          />
          <Tab
            label={
              <Box>
                <BusinessIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                Companies
              </Box>
            }
          />
          <Tab
            label={
              <Box>
                <PeopleIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                Employees
              </Box>
            }
          />
          <Tab
            label={
              <Box>
                <RoutesIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                Routes / Crews
              </Box>
            }
          />
        </Tabs>

        {/* Properties Tab */}
        <TabPanel value={currentTab} index={0}>
          <Paper className={classes.syncSection} elevation={0}>
            <Typography variant="h6" gutterBottom>
              Property Sync
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Sync Aspire properties and match them to Portal jobsites
            </Typography>

            {propertyLoading && <LinearProgress style={{ marginBottom: 16 }} />}

            <Grid container spacing={3} className={classes.statsContainer}>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Box textAlign="center">
                      <Typography className={classes.statNumber}>
                        {propertyStats?.totalProperties || 0}
                      </Typography>
                      <Typography variant="subtitle2" className={classes.statLabel}>
                        Total Aspire Properties
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Box textAlign="center">
                      <Typography className={classes.statNumber} style={{ color: '#4caf50' }}>
                        {propertyStats?.matchedProperties || 0}
                      </Typography>
                      <Typography variant="subtitle2" className={classes.statLabel}>
                        Matched to Jobsites
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Box textAlign="center">
                      <Typography className={classes.statNumber} style={{ color: '#ff9800' }}>
                        {propertyStats?.unmatchedProperties || 0}
                      </Typography>
                      <Typography variant="subtitle2" className={classes.statLabel}>
                        Unmatched
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Box textAlign="center">
                      <Typography className={classes.statNumber} style={{ color: '#2196f3' }}>
                        {propertyStats?.matchPercentage || 0}%
                      </Typography>
                      <Typography variant="subtitle2" className={classes.statLabel}>
                        Match Rate
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SyncIcon />}
                onClick={syncProperties}
                disabled={propertyLoading}
              >
                {propertyLoading ? 'Syncing...' : 'Sync Properties'}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<HistoryIcon />}
                onClick={backfillVisitDates}
                disabled={propertyLoading}
              >
                Backfill Visit Dates
              </Button>
            </Box>

            {/* Progress indicator for background job */}
            {jobProgress && (
              <Card style={{ marginTop: 16, backgroundColor: '#e3f2fd' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                      {jobProgress.message}
                    </Typography>
                    <Typography variant="subtitle2" color="primary" style={{ fontWeight: 600 }}>
                      {jobProgress.percentage}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={jobProgress.percentage}
                    style={{ height: 8, borderRadius: 4 }}
                  />
                </CardContent>
              </Card>
            )}

            <Alert severity="info" style={{ marginTop: 16 }}>
              <strong>Note:</strong> Property matching uses address-based geo-distance and name similarity.
              <br /><strong>Workflow:</strong> Sync Properties → Properties are auto-matched to jobsites
            </Alert>

            {/* Unmatched Properties Table */}
            {unmatchedProperties && unmatchedProperties.length > 0 && (
              <Box mt={4}>
                <Divider style={{ margin: '24px 0' }} />
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Unmatched Properties ({unmatchedPropertiesTotal})
                  </Typography>
                  <TextField
                    placeholder="Search properties..."
                    variant="outlined"
                    size="small"
                    value={propertiesSearchQuery}
                    onChange={(e) => setPropertiesSearchQuery(e.target.value)}
                    style={{ width: '300px' }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      )
                    }}
                  />
                </Box>
                <Typography variant="body2" color="textSecondary" paragraph>
                  These Aspire properties have not been matched to Portal jobsites yet
                </Typography>
                <TableContainer component={Paper} className={classes.tableContainer}>
                  <Table>
                    <TableHead className={classes.tableHeader}>
                      <TableRow>
                        <TableCell>Property ID</TableCell>
                        <TableCell>Property Name</TableCell>
                        <TableCell>Address</TableCell>
                        <TableCell>Branch</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {unmatchedProperties
                        .filter((property) => {
                          if (!propertiesSearchQuery) return true;
                          const query = propertiesSearchQuery.toLowerCase();
                          return (
                            property.PropertyName?.toLowerCase().includes(query) ||
                            property.PropertyAddressLine1?.toLowerCase().includes(query) ||
                            property.PropertyAddressCity?.toLowerCase().includes(query) ||
                            property.PropertyID?.toString().includes(query)
                          );
                        })
                        .map((property) => (
                        <TableRow key={property.PropertyID} className={classes.tableRow}>
                          <TableCell>{property.PropertyID}</TableCell>
                          <TableCell>
                            <strong>{property.PropertyName || property.PropertyAddressLine1}</strong>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {property.PropertyAddressLine1}
                              {property.PropertyAddressLine2 && `, ${property.PropertyAddressLine2}`}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {property.PropertyAddressCity}, {property.PropertyAddressState} {property.PropertyAddressZip}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={`Branch ${property.BranchID}`}
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label="Active"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              startIcon={<LinkIcon />}
                              onClick={() => openPropertyMatchDialog(property)}
                            >
                              Match
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={unmatchedPropertiesTotal}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </TableContainer>
              </Box>
            )}
          </Paper>
        </TabPanel>

        {/* Contacts Tab */}
        <TabPanel value={currentTab} index={1}>
          <Paper className={classes.syncSection} elevation={0}>
            <Typography variant="h6" gutterBottom>
              Contact Sync
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Sync Aspire contacts and match them to Portal customers
            </Typography>

            {contactLoading && <LinearProgress style={{ marginBottom: 16 }} />}

            <Grid container spacing={3} className={classes.statsContainer}>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Box textAlign="center">
                      <Typography className={classes.statNumber}>
                        {contactStats?.totalContacts || 0}
                      </Typography>
                      <Typography variant="subtitle2" className={classes.statLabel}>
                        Total Contacts
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Box textAlign="center">
                      <Typography className={classes.statNumber} style={{ color: '#4caf50' }}>
                        {contactStats?.matchedContacts || 0}
                      </Typography>
                      <Typography variant="subtitle2" className={classes.statLabel}>
                        Matched
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Box textAlign="center">
                      <Typography className={classes.statNumber} style={{ color: '#ff9800' }}>
                        {contactStats?.unmatchedContacts || 0}
                      </Typography>
                      <Typography variant="subtitle2" className={classes.statLabel}>
                        Unmatched
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Box textAlign="center">
                      <Typography className={classes.statNumber} style={{ color: '#2196f3' }}>
                        {contactStats?.matchPercentage || 0}%
                      </Typography>
                      <Typography variant="subtitle2" className={classes.statLabel}>
                        Match Rate
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SyncIcon />}
                onClick={syncContacts}
                disabled={contactLoading}
              >
                {contactLoading ? 'Syncing...' : 'Sync Contacts'}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<CheckCircleIcon />}
                onClick={matchContacts}
                disabled={contactLoading}
              >
                {contactLoading ? 'Matching...' : 'Match Contacts'}
              </Button>
            </Box>

            <Alert severity="info" style={{ marginTop: 16 }}>
              <strong>Note:</strong> Contact matching uses case-insensitive email and name matching.
              When 80%+ of a company's contacts match to the same Portal customer, the company is automatically matched too!
              <br /><strong>Workflow:</strong> Sync Contacts → Match Contacts (auto-matches companies via consensus)
            </Alert>

            {/* Unmatched Contacts Table */}
            {unmatchedContacts.length > 0 && (
              <Box mt={4}>
                <Divider style={{ margin: '24px 0' }} />
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Unmatched Contacts ({unmatchedContactsTotal})
                  </Typography>
                  <TextField
                    placeholder="Search contacts..."
                    variant="outlined"
                    size="small"
                    value={contactsSearchQuery}
                    onChange={(e) => setContactsSearchQuery(e.target.value)}
                    style={{ width: '300px' }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      )
                    }}
                  />
                </Box>
                <Typography variant="body2" color="textSecondary" paragraph>
                  These Aspire contacts have not been matched to Portal contacts yet
                </Typography>
                <TableContainer component={Paper} className={classes.tableContainer}>
                  <Table>
                    <TableHead className={classes.tableHeader}>
                      <TableRow>
                        <TableCell>Contact ID</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Company</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Branch</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {unmatchedContacts
                        .filter((contact) => {
                          if (!contactsSearchQuery) return true;
                          const query = contactsSearchQuery.toLowerCase();
                          const fullName = `${contact.FirstName || ''} ${contact.LastName || ''}`.trim();
                          return (
                            contact.ContactName?.toLowerCase().includes(query) ||
                            fullName.toLowerCase().includes(query) ||
                            contact.Email?.toLowerCase().includes(query) ||
                            contact.CompanyName?.toLowerCase().includes(query) ||
                            contact.Phone?.toLowerCase().includes(query) ||
                            contact.ContactID?.toString().includes(query)
                          );
                        })
                        .map((contact) => (
                        <TableRow key={contact.ContactID} className={classes.tableRow}>
                          <TableCell>{contact.ContactID}</TableCell>
                          <TableCell>
                            {contact.ContactName || `${contact.FirstName || ''} ${contact.LastName || ''}`.trim()}
                            {!contact.hasName && (
                              <Chip
                                size="small"
                                label="No Name"
                                color="default"
                                style={{ marginLeft: 8, fontSize: '0.7rem' }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {contact.Email || (
                              <Chip
                                size="small"
                                label="No Email"
                                color="secondary"
                                style={{ fontSize: '0.7rem' }}
                              />
                            )}
                          </TableCell>
                          <TableCell>{contact.CompanyName || '-'}</TableCell>
                          <TableCell>{contact.Phone || contact.Mobile || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={`Branch ${contact.BranchID}`}
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={contact.IsActive ? 'Active' : 'Inactive'}
                              color={contact.IsActive ? 'primary' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              startIcon={<LinkIcon />}
                              onClick={() => openContactMatchDialog(contact)}
                            >
                              Match
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={unmatchedContactsTotal}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </TableContainer>
              </Box>
            )}
          </Paper>
        </TabPanel>

        {/* Companies Tab */}
        <TabPanel value={currentTab} index={2}>
          <Paper className={classes.syncSection} elevation={0}>
            <Typography variant="h6" gutterBottom>
              Company Sync
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Sync Aspire companies and match them to Portal customers via contact relationships
            </Typography>

            {companyLoading && <LinearProgress style={{ marginBottom: 16 }} />}

            <Grid container spacing={3} className={classes.statsContainer}>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Box textAlign="center">
                      <Typography className={classes.statNumber}>
                        {companyStats?.activeCompanies || 0}
                      </Typography>
                      <Typography variant="subtitle2" className={classes.statLabel}>
                        Active Companies
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Box textAlign="center">
                      <Typography className={classes.statNumber} style={{ color: '#4caf50' }}>
                        {companyStats?.matchedCompanies || 0}
                      </Typography>
                      <Typography variant="subtitle2" className={classes.statLabel}>
                        Matched
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Box textAlign="center">
                      <Typography className={classes.statNumber} style={{ color: '#ff9800' }}>
                        {companyStats?.unmatchedActiveCompanies || 0}
                      </Typography>
                      <Typography variant="subtitle2" className={classes.statLabel}>
                        Unmatched
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Box textAlign="center">
                      <Typography className={classes.statNumber} style={{ color: '#2196f3' }}>
                        {companyStats?.matchPercentage || 0}%
                      </Typography>
                      <Typography variant="subtitle2" className={classes.statLabel}>
                        Match Rate
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SyncIcon />}
                onClick={syncCompanies}
                disabled={companyLoading}
              >
                {companyLoading ? 'Syncing...' : 'Sync Companies'}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<CheckCircleIcon />}
                onClick={matchCompanies}
                disabled={companyLoading}
              >
                {companyLoading ? 'Matching...' : 'Match Companies'}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<LinkIcon />}
                onClick={backfillCompanyMatches}
                disabled={companyLoading}
              >
                Link via Contacts/Properties
              </Button>
            </Box>

            <Alert severity="info" style={{ marginTop: 16 }}>
              <strong>Branch Filtering:</strong> Only companies with contacts in Branch 4 & 5 are synced.
              <br /><strong>Auto-Matching:</strong> Many companies are already auto-matched when you match contacts!
              <br /><strong>Smart Linking:</strong> Use "Link via Contacts/Properties" to automatically link companies when ALL their matched contacts/properties point to the same customer.
              <br />The "Match Companies" button provides additional matching strategies:
              <br />1. Company name matching (CompanyName → Customer displayName/searchName)
              <br />2. Manual contact relationship analysis
              <br /><strong>Tip:</strong> After syncing and matching contacts/properties, run "Link via Contacts/Properties" to auto-link obvious companies!
            </Alert>

            {/* Unmatched Companies Table */}
            {unmatchedCompanies.length > 0 && (
              <Box mt={4}>
                <Divider style={{ margin: '24px 0' }} />
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Unmatched Companies ({unmatchedCompaniesTotal})
                  </Typography>
                  <TextField
                    placeholder="Search companies..."
                    variant="outlined"
                    size="small"
                    value={companiesSearchQuery}
                    onChange={(e) => setCompaniesSearchQuery(e.target.value)}
                    style={{ width: '300px' }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      )
                    }}
                  />
                </Box>
                <Typography variant="body2" color="textSecondary" paragraph>
                  These Aspire companies have not been matched to Portal customers yet
                </Typography>
                <TableContainer component={Paper} className={classes.tableContainer}>
                  <Table>
                    <TableHead className={classes.tableHeader}>
                      <TableRow>
                        <TableCell>Company ID</TableCell>
                        <TableCell>Company Name</TableCell>
                        <TableCell>Contacts</TableCell>
                        <TableCell>Properties</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {unmatchedCompanies
                        .filter((company) => {
                          if (!companiesSearchQuery) return true;
                          const query = companiesSearchQuery.toLowerCase();
                          return (
                            company.CompanyName?.toLowerCase().includes(query) ||
                            company.CompanyID?.toString().includes(query)
                          );
                        })
                        .map((company) => (
                        <TableRow key={company.CompanyID} className={classes.tableRow}>
                          <TableCell>{company.CompanyID}</TableCell>
                          <TableCell>
                            <strong>{company.CompanyName}</strong>
                          </TableCell>
                          <TableCell>
                            {company.contactCount > 0 ? (
                              <Chip
                                size="small"
                                label={`${company.contactCount} contacts`}
                                color="primary"
                                variant="outlined"
                                clickable
                                onClick={() => openDetailsDialog(company, 'contacts')}
                                style={{ cursor: 'pointer' }}
                              />
                            ) : (
                              <Chip
                                size="small"
                                label="No contacts"
                                color="default"
                                style={{ fontSize: '0.7rem' }}
                              />
                            )}
                            {company.matchedContactCount > 0 && (
                              <Chip
                                size="small"
                                label={`${company.matchedContactCount} matched`}
                                color="primary"
                                style={{ fontSize: '0.7rem', marginLeft: 4 }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {company.propertyCount > 0 ? (
                              <Chip
                                size="small"
                                label={`${company.propertyCount} properties`}
                                color="secondary"
                                variant="outlined"
                                clickable
                                onClick={() => openDetailsDialog(company, 'properties')}
                                style={{ cursor: 'pointer' }}
                              />
                            ) : (
                              <Chip
                                size="small"
                                label="No properties"
                                color="default"
                                style={{ fontSize: '0.7rem' }}
                              />
                            )}
                            {company.matchedPropertyCount > 0 && (
                              <Chip
                                size="small"
                                label={`${company.matchedPropertyCount} matched`}
                                color="secondary"
                                style={{ fontSize: '0.7rem', marginLeft: 4 }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={company.Active ? 'Active' : 'Inactive'}
                              color={company.Active ? 'primary' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={1}>
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={<LinkIcon />}
                                onClick={() => openMatchDialog(company)}
                              >
                                Match
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={async () => {
                                  const message = company.contactCount > 0 || company.hasMatchedContacts
                                    ? `Create a new customer for "${company.CompanyName}"?\n\nThis will also create:\n• ${company.contactCount} contacts\n• Properties associated with this company`
                                    : `Create a new customer for "${company.CompanyName}"?`;

                                  if (window.confirm(message)) {
                                    try {
                                      const response = await axios.post('/api/aspire/companies/create-customer', {
                                        companyId: company.CompanyID,
                                        includeContactsAndProperties: true
                                      });
                                      if (response.data.success) {
                                        const { summary } = response.data;
                                        showSnackbar(
                                          `✓ Customer ${summary.customer.agId} created with ${summary.contacts.created} contacts and ${summary.properties.created} jobsites!`,
                                          'success'
                                        );
                                        await loadCompanyStatus();
                                        await loadUnmatchedCompanies();
                                      }
                                    } catch (error) {
                                      showSnackbar(
                                        error.response?.data?.error || 'Failed to create customer',
                                        'error'
                                      );
                                    }
                                  }
                                }}
                              >
                                Create New
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={unmatchedCompaniesTotal}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </TableContainer>
              </Box>
            )}
          </Paper>
        </TabPanel>

        {/* Employees Tab */}
        <TabPanel value={currentTab} index={3}>
          <Paper className={classes.syncSection} elevation={0}>
            <Typography variant="h6" gutterBottom>
              Employee Sync
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Sync Aspire employee contacts and match them to Portal employees
            </Typography>

            {employeeLoading && <LinearProgress style={{ marginBottom: 16 }} />}

            <Grid container spacing={3} className={classes.statsContainer}>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Box textAlign="center">
                      <Typography className={classes.statNumber}>
                        {employeeStats?.totalEmployees || 0}
                      </Typography>
                      <Typography variant="subtitle2" className={classes.statLabel}>
                        Total Employee Contacts
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Box textAlign="center">
                      <Typography className={classes.statNumber} style={{ color: '#4caf50' }}>
                        {employeeStats?.matched || 0}
                      </Typography>
                      <Typography variant="subtitle2" className={classes.statLabel}>
                        Matched to Employees
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Box textAlign="center">
                      <Typography className={classes.statNumber} style={{ color: '#ff9800' }}>
                        {employeeStats?.unmatched || 0}
                      </Typography>
                      <Typography variant="subtitle2" className={classes.statLabel}>
                        Unmatched
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Box textAlign="center">
                      <Typography className={classes.statNumber} style={{ color: '#2196f3' }}>
                        {employeeStats?.matchPercentage || 0}%
                      </Typography>
                      <Typography variant="subtitle2" className={classes.statLabel}>
                        Match Rate
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Unmatched Employees Table */}
            {unmatchedEmployees.length > 0 && (
              <Box mt={4}>
                <Divider style={{ margin: '24px 0' }} />
                <Typography variant="h6" gutterBottom>
                  Unmatched Employee Contacts ({unmatchedEmployeesTotal})
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  These Aspire employee contacts have not been matched to Portal employees yet
                </Typography>
                <TableContainer component={Paper} className={classes.tableContainer}>
                  <Table>
                    <TableHead className={classes.tableHeader}>
                      <TableRow>
                        <TableCell>Contact ID</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Branch</TableCell>
                        <TableCell>Employee #</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {unmatchedEmployees.map((employee) => (
                        <TableRow key={employee.ContactID} className={classes.tableRow}>
                          <TableCell>{employee.ContactID}</TableCell>
                          <TableCell>
                            <strong>
                              {employee.ContactName || `${employee.FirstName || ''} ${employee.LastName || ''}`.trim()}
                            </strong>
                          </TableCell>
                          <TableCell>{employee.Email || '-'}</TableCell>
                          <TableCell>{employee.Phone || employee.Mobile || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={`Branch ${employee.BranchID}`}
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{employee.EmployeeNumber || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={unmatchedEmployeesTotal}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              </Box>
            )}
          </Paper>
        </TabPanel>

        {/* Routes / Crews Tab */}
        <TabPanel value={currentTab} index={4}>
          <Paper className={classes.syncSection} elevation={0}>
            <Typography variant="h6" gutterBottom>
              Route / Crew Matching
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Match Aspire routes to Portal crews. Routes are synced from Aspire with a crew leader reference — match them to the correct Portal crew.
            </Typography>

            {routeLoading && <LinearProgress style={{ marginBottom: 16 }} />}

            <Grid container spacing={3} className={classes.statsContainer}>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Box textAlign="center">
                      <Typography className={classes.statNumber}>
                        {routeStats?.totalRoutes || 0}
                      </Typography>
                      <Typography variant="subtitle2" className={classes.statLabel}>
                        Total Active Routes
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Box textAlign="center">
                      <Typography className={classes.statNumber} style={{ color: '#4caf50' }}>
                        {routeStats?.matchedRoutes || 0}
                      </Typography>
                      <Typography variant="subtitle2" className={classes.statLabel}>
                        Matched to Crews
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Box textAlign="center">
                      <Typography className={classes.statNumber} style={{ color: '#ff9800' }}>
                        {routeStats?.unmatchedRoutes || 0}
                      </Typography>
                      <Typography variant="subtitle2" className={classes.statLabel}>
                        Unmatched
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Box textAlign="center">
                      <Typography className={classes.statNumber} style={{ color: '#2196f3' }}>
                        {routeStats?.matchPercentage || 0}%
                      </Typography>
                      <Typography variant="subtitle2" className={classes.statLabel}>
                        Match Rate
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<CheckCircleIcon />}
                onClick={autoMatchRoutes}
                disabled={routeLoading}
              >
                {routeLoading ? 'Matching...' : 'Auto-Match Routes'}
              </Button>
            </Box>

            <Alert severity="info" style={{ marginTop: 16 }}>
              <strong>Auto-Match</strong> tries to link routes to crews by: foreman employee match, crew leader name, and route number.
              Use the manual <strong>Match</strong> button for any remaining unmatched routes.
            </Alert>

            {/* Unmatched Routes Table */}
            {unmatchedRoutes.length > 0 && (
              <Box mt={4}>
                <Divider style={{ margin: '24px 0' }} />
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Unmatched Routes ({unmatchedRoutesTotal})
                  </Typography>
                  <TextField
                    placeholder="Search routes..."
                    variant="outlined"
                    size="small"
                    value={routesSearchQuery}
                    onChange={(e) => {
                      setRoutesSearchQuery(e.target.value);
                      setPage(0);
                    }}
                    style={{ width: '300px' }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      )
                    }}
                  />
                </Box>
                <Typography variant="body2" color="textSecondary" paragraph>
                  These Aspire routes have not been matched to Portal crews yet
                </Typography>
                <TableContainer component={Paper} className={classes.tableContainer}>
                  <Table>
                    <TableHead className={classes.tableHeader}>
                      <TableRow>
                        <TableCell>Route ID</TableCell>
                        <TableCell>Route Name</TableCell>
                        <TableCell>Crew Leader</TableCell>
                        <TableCell>Division</TableCell>
                        <TableCell>Size</TableCell>
                        <TableCell>Branch</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {unmatchedRoutes
                        .filter((route) => {
                          if (!routesSearchQuery) return true;
                          const query = routesSearchQuery.toLowerCase();
                          return (
                            route.RouteName?.toLowerCase().includes(query) ||
                            route.CrewLeaderName?.toLowerCase().includes(query) ||
                            route.DivisionName?.toLowerCase().includes(query) ||
                            route.RouteID?.toString().includes(query)
                          );
                        })
                        .map((route) => (
                        <TableRow key={route.RouteID} className={classes.tableRow}>
                          <TableCell>{route.RouteID}</TableCell>
                          <TableCell>
                            <strong>{route.RouteName || `Route ${route.RouteID}`}</strong>
                          </TableCell>
                          <TableCell>
                            {route.CrewLeaderName || '-'}
                            {route.CrewLeaderContactID && (
                              <Typography variant="caption" display="block" color="textSecondary">
                                Contact ID: {route.CrewLeaderContactID}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {route.DivisionName && (
                              <Chip
                                size="small"
                                label={route.DivisionName}
                                color="default"
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                          <TableCell>{route.RouteSize || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={`Branch ${route.BranchID}`}
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={1}>
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={<LinkIcon />}
                                onClick={() => openRouteMatchDialog(route)}
                              >
                                Match
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                startIcon={<AddCrewIcon />}
                                onClick={() => handleCreateCrewFromRoute(route)}
                                disabled={routeLoading}
                              >
                                Create Crew
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={unmatchedRoutesTotal}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </TableContainer>
              </Box>
            )}
          </Paper>
        </TabPanel>
      </Paper>

      {/* Details Dialog (Contacts or Properties) */}
      <Dialog
        open={detailsDialogOpen}
        onClose={closeDetailsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {detailsType === 'contacts' && (
            <Box display="flex" alignItems="center" gap={1}>
              <ContactsIcon color="primary" />
              <Typography variant="h6">
                Contacts for {selectedCompanyForDetails?.CompanyName}
              </Typography>
            </Box>
          )}
          {detailsType === 'properties' && (
            <Box display="flex" alignItems="center" gap={1}>
              <HomeIcon color="secondary" />
              <Typography variant="h6">
                Properties for {selectedCompanyForDetails?.CompanyName}
              </Typography>
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {detailsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {detailsType === 'contacts' && detailsData.length > 0 && (
                <List>
                  {detailsData.map((contact) => (
                    <ListItem
                      key={contact.ContactID}
                      style={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 4,
                        marginBottom: 8
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="subtitle1">
                              {contact.ContactName || `${contact.FirstName || ''} ${contact.LastName || ''}`.trim()}
                            </Typography>
                            {contact.matchedContactId && (
                              <Chip
                                size="small"
                                label="Matched"
                                color="primary"
                                style={{ fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <>
                            {contact.Email && (
                              <Typography variant="body2" component="div">
                                Email: {contact.Email}
                              </Typography>
                            )}
                            {contact.Phone && (
                              <Typography variant="body2" component="div">
                                Phone: {contact.Phone}
                              </Typography>
                            )}
                            {contact.Mobile && (
                              <Typography variant="body2" component="div">
                                Mobile: {contact.Mobile}
                              </Typography>
                            )}
                            <Typography variant="caption" color="textSecondary">
                              Contact ID: {contact.ContactID}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}

              {detailsType === 'properties' && detailsData.length > 0 && (
                <List>
                  {detailsData.map((property) => (
                    <ListItem
                      key={property.PropertyID}
                      style={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 4,
                        marginBottom: 8
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="subtitle1">
                              {property.PropertyName || property.AddressLine1}
                            </Typography>
                            {property.matchedJobsiteId && (
                              <Chip
                                size="small"
                                label="Matched"
                                color="secondary"
                                style={{ fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" component="div">
                              {property.AddressLine1}
                              {property.AddressLine2 && `, ${property.AddressLine2}`}
                            </Typography>
                            <Typography variant="body2" component="div">
                              {property.City}, {property.StateProvinceCode} {property.ZipCode}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Property ID: {property.PropertyID}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}

              {detailsData.length === 0 && !detailsLoading && (
                <Alert severity="info">
                  No {detailsType} found for this company.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetailsDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Conflict Resolution Dialog */}
      <Dialog
        open={conflictDialogOpen}
        onClose={closeConflictDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="secondary" />
            <Typography variant="h6">Match Conflict Detected</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" style={{ marginBottom: 24 }}>
            The customer you selected is already matched to a different Aspire company.
            Please review both matches and choose which one is correct.
          </Alert>

          {conflictData && (
            <Grid container spacing={3}>
              {/* Existing Match */}
              <Grid item xs={12} md={6}>
                <Paper style={{ padding: 16, backgroundColor: '#fff3e0', border: '2px solid #ff9800' }}>
                  <Typography variant="h6" gutterBottom color="secondary">
                    Current Match (Will Be Removed)
                  </Typography>
                  <Divider style={{ marginBottom: 16 }} />

                  <Box mb={2}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Aspire Company:
                    </Typography>
                    <Typography variant="body1">
                      <strong>{conflictData.existingMatch.aspireCompany.CompanyName}</strong>
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      ID: {conflictData.existingMatch.aspireCompany.CompanyID}
                    </Typography>
                    <Chip
                      size="small"
                      label={conflictData.existingMatch.aspireCompany.Active ? 'Active' : 'Inactive'}
                      color={conflictData.existingMatch.aspireCompany.Active ? 'primary' : 'default'}
                      style={{ marginTop: 8 }}
                    />
                  </Box>

                  <Typography variant="subtitle2" color="textSecondary" style={{ marginTop: 16 }}>
                    Matched To:
                  </Typography>
                  <Box mt={1}>
                    <Typography variant="body1">
                      {conflictData.existingMatch.customer.displayName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      AG {conflictData.existingMatch.customer.agId}
                    </Typography>
                    {conflictData.existingMatch.customer.email && (
                      <Typography variant="body2" color="textSecondary">
                        {conflictData.existingMatch.customer.email}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* Proposed Match */}
              <Grid item xs={12} md={6}>
                <Paper style={{ padding: 16, backgroundColor: '#e8f5e9', border: '2px solid #4caf50' }}>
                  <Typography variant="h6" gutterBottom style={{ color: '#4caf50' }}>
                    New Match (Will Be Applied)
                  </Typography>
                  <Divider style={{ marginBottom: 16 }} />

                  <Box mb={2}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Aspire Company:
                    </Typography>
                    <Typography variant="body1">
                      <strong>{conflictData.proposedMatch.aspireCompany.CompanyName}</strong>
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      ID: {conflictData.proposedMatch.aspireCompany.CompanyID}
                    </Typography>
                    <Chip
                      size="small"
                      label={conflictData.proposedMatch.aspireCompany.Active ? 'Active' : 'Inactive'}
                      color={conflictData.proposedMatch.aspireCompany.Active ? 'primary' : 'default'}
                      style={{ marginTop: 8 }}
                    />
                  </Box>

                  <Typography variant="subtitle2" color="textSecondary" style={{ marginTop: 16 }}>
                    Will Be Matched To:
                  </Typography>
                  <Box mt={1}>
                    <Typography variant="body1">
                      {conflictData.proposedMatch.customer.displayName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      AG {conflictData.proposedMatch.customer.agId}
                    </Typography>
                    {conflictData.proposedMatch.customer.email && (
                      <Typography variant="body2" color="textSecondary">
                        {conflictData.proposedMatch.customer.email}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}

          <Alert severity="info" style={{ marginTop: 24 }}>
            <strong>Note:</strong> Confirming this change will:
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              <li>Remove the existing match between "{conflictData?.existingMatch.aspireCompany.CompanyName}" and the customer</li>
              <li>Create a new match between "{conflictData?.proposedMatch.aspireCompany.CompanyName}" and the customer</li>
            </ul>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConflictDialog} color="default">
            Cancel (Keep Existing Match)
          </Button>
          <Button
            onClick={handleConfirmReplaceMatch}
            color="primary"
            variant="contained"
          >
            Confirm (Replace Match)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Match Company Dialog */}
      <Dialog
        open={matchDialogOpen}
        onClose={closeMatchDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Match Company to Customer
          {selectedCompany && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              Finding a match for: <strong>{selectedCompany.CompanyName}</strong>
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            autoFocus
            label="Search Customers"
            placeholder="Type customer name, company name, or AG ID..."
            value={customerSearchQuery}
            onChange={handleSearchInputChange}
            variant="outlined"
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchLoading && (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              )
            }}
          />

          {customerSearchQuery.length >= 2 && (
            <Box mt={2}>
              {customerSearchResults.length > 0 ? (
                <>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Found {customerSearchResults.length} customer(s)
                  </Typography>
                  <List>
                    {customerSearchResults.map((customer) => (
                      <ListItem
                        key={customer._id}
                        button
                        onClick={() => handleManualMatch(customer)}
                        style={{
                          border: '1px solid #e0e0e0',
                          borderRadius: 4,
                          marginBottom: 8
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="subtitle1">
                                {customer.displayName || customer.companyName}
                              </Typography>
                              <Chip
                                size="small"
                                label={`AG ${customer.agId}`}
                                color="primary"
                                variant="outlined"
                              />
                              {customer.aspireCompanyId && (
                                <Chip
                                  size="small"
                                  label="Already matched"
                                  color="secondary"
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <>
                              {customer.companyName && customer.companyName !== customer.displayName && (
                                <Typography variant="body2" component="span">
                                  Company: {customer.companyName}
                                  <br />
                                </Typography>
                              )}
                              {customer.email && (
                                <Typography variant="body2" component="span">
                                  Email: {customer.email}
                                </Typography>
                              )}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              ) : (
                !searchLoading && (
                  <Alert severity="info">
                    No customers found matching "{customerSearchQuery}". Try a different search term or create a new customer.
                  </Alert>
                )
              )}
            </Box>
          )}

          {customerSearchQuery.length < 2 && customerSearchQuery.length > 0 && (
            <Alert severity="info" style={{ marginTop: 16 }}>
              Please enter at least 2 characters to search
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMatchDialog} color="default">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset All Data Confirmation Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => !resetLoading && setResetDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon style={{ color: '#ff5252' }} />
            <Typography variant="h6">Reset All Aspire Data</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" style={{ marginBottom: 16 }}>
            <strong>WARNING:</strong> This action cannot be undone! This will permanently delete ALL Aspire data and remove all references from your Portal database.
          </Alert>

          {cleanupStatus && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Aspire Collections (will be deleted):</strong>
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary={`${cleanupStatus.aspireCollections.companies} Aspire Companies`}
                    secondary="All synced company records from Aspire"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={`${cleanupStatus.aspireCollections.contacts} Aspire Contacts`}
                    secondary="All synced contact records from Aspire"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={`${cleanupStatus.aspireCollections.properties} Aspire Properties`}
                    secondary="All synced property records from Aspire"
                  />
                </ListItem>
              </List>

              {cleanupStatus.autoCreatedEntities && (
                (cleanupStatus.autoCreatedEntities.customers > 0 ||
                 cleanupStatus.autoCreatedEntities.contacts > 0 ||
                 cleanupStatus.autoCreatedEntities.jobsites > 0 ||
                 cleanupStatus.autoCreatedEntities.employees > 0) && (
                  <>
                    <Divider style={{ margin: '16px 0' }} />
                    <Typography variant="subtitle1" gutterBottom>
                      <strong style={{ color: '#ff5252' }}>Auto-Created Portal Entities (will be DELETED):</strong>
                    </Typography>
                    <List dense>
                      {cleanupStatus.autoCreatedEntities.customers > 0 && (
                        <ListItem>
                          <ListItemText
                            primary={`${cleanupStatus.autoCreatedEntities.customers} Customers`}
                            secondary="Customers that were auto-created from Aspire data"
                            primaryTypographyProps={{ style: { color: '#ff5252' } }}
                          />
                        </ListItem>
                      )}
                      {cleanupStatus.autoCreatedEntities.contacts > 0 && (
                        <ListItem>
                          <ListItemText
                            primary={`${cleanupStatus.autoCreatedEntities.contacts} Contacts`}
                            secondary="Contacts that were auto-created from Aspire data"
                            primaryTypographyProps={{ style: { color: '#ff5252' } }}
                          />
                        </ListItem>
                      )}
                      {cleanupStatus.autoCreatedEntities.jobsites > 0 && (
                        <ListItem>
                          <ListItemText
                            primary={`${cleanupStatus.autoCreatedEntities.jobsites} Jobsites`}
                            secondary="Jobsites that were auto-created from Aspire data"
                            primaryTypographyProps={{ style: { color: '#ff5252' } }}
                          />
                        </ListItem>
                      )}
                      {cleanupStatus.autoCreatedEntities.employees > 0 && (
                        <ListItem>
                          <ListItemText
                            primary={`${cleanupStatus.autoCreatedEntities.employees} Employees`}
                            secondary="Employees that were auto-created from Aspire data"
                            primaryTypographyProps={{ style: { color: '#ff5252' } }}
                          />
                        </ListItem>
                      )}
                    </List>
                  </>
                )
              )}

              {cleanupStatus.portalReferences && (
                (cleanupStatus.portalReferences.customers > 0 ||
                 cleanupStatus.portalReferences.contacts > 0 ||
                 cleanupStatus.portalReferences.jobsites > 0 ||
                 cleanupStatus.portalReferences.employees > 0) && (
                  <>
                    <Divider style={{ margin: '16px 0' }} />
                    <Typography variant="subtitle1" gutterBottom>
                      <strong>Existing Portal Entities (references will be cleared):</strong>
                    </Typography>
                    <List dense>
                      {cleanupStatus.portalReferences.customers > 0 && (
                        <ListItem>
                          <ListItemText
                            primary={`${cleanupStatus.portalReferences.customers} Customers`}
                            secondary="Aspire references will be removed (customers kept)"
                          />
                        </ListItem>
                      )}
                      {cleanupStatus.portalReferences.contacts > 0 && (
                        <ListItem>
                          <ListItemText
                            primary={`${cleanupStatus.portalReferences.contacts} Contacts`}
                            secondary="Aspire references will be removed (contacts kept)"
                          />
                        </ListItem>
                      )}
                      {cleanupStatus.portalReferences.jobsites > 0 && (
                        <ListItem>
                          <ListItemText
                            primary={`${cleanupStatus.portalReferences.jobsites} Jobsites`}
                            secondary="Aspire references will be removed (jobsites kept)"
                          />
                        </ListItem>
                      )}
                      {cleanupStatus.portalReferences.employees > 0 && (
                        <ListItem>
                          <ListItemText
                            primary={`${cleanupStatus.portalReferences.employees} Employees`}
                            secondary="Aspire references will be removed (employees kept)"
                          />
                        </ListItem>
                      )}
                    </List>
                  </>
                )
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setResetDialogOpen(false)}
            disabled={resetLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResetAll}
            color="secondary"
            variant="contained"
            disabled={resetLoading}
            style={{ backgroundColor: '#ff5252' }}
          >
            {resetLoading ? <CircularProgress size={24} /> : 'Yes, Delete Everything'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Property Match Dialog */}
      <Dialog
        open={propertyMatchDialogOpen}
        onClose={() => setPropertyMatchDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Match Property to Jobsite
          {selectedProperty && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              Finding a match for: <strong>{selectedProperty.PropertyName || selectedProperty.PropertyAddressLine1}</strong>
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedProperty && (
            <Box mb={3} p={2} style={{ backgroundColor: '#f5f5f5', borderRadius: 4 }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Property Details:
              </Typography>
              <Typography variant="body2">
                <strong>Property ID:</strong> {selectedProperty.PropertyID}
              </Typography>
              <Typography variant="body2">
                <strong>Address:</strong> {selectedProperty.PropertyAddressLine1}
                {selectedProperty.PropertyAddressLine2 && `, ${selectedProperty.PropertyAddressLine2}`}
              </Typography>
              <Typography variant="body2">
                {selectedProperty.PropertyAddressCity}, {selectedProperty.PropertyAddressState} {selectedProperty.PropertyAddressZip}
              </Typography>
              <Typography variant="body2">
                <strong>Branch:</strong> {selectedProperty.BranchID}
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            autoFocus
            label="Search Jobsites"
            placeholder="Type jobsite name or address..."
            value={jobsiteSearchQuery}
            onChange={(e) => {
              const query = e.target.value;
              setJobsiteSearchQuery(query);
              searchJobsites(query);
            }}
            variant="outlined"
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: jobsiteSearchLoading && (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              )
            }}
          />

          {jobsiteSearchQuery.length >= 2 && (
            <Box mt={2}>
              {jobsiteSearchResults.length > 0 ? (
                <>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Found {jobsiteSearchResults.length} jobsite(s)
                  </Typography>
                  <List>
                    {jobsiteSearchResults.map((jobsite) => (
                      <ListItem
                        key={jobsite._id}
                        button
                        onClick={() => handlePropertyMatch(jobsite)}
                        style={{
                          border: '1px solid #e0e0e0',
                          borderRadius: 4,
                          marginBottom: 8
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                              <Typography variant="subtitle1">
                                {jobsite.name}
                              </Typography>
                              {jobsite.agId && (
                                <Chip
                                  size="small"
                                  label={`AG ${jobsite.agId}`}
                                  color="primary"
                                  variant="outlined"
                                />
                              )}
                              {jobsite.buildingNumber && (
                                <Chip
                                  size="small"
                                  label={`Bldg #${jobsite.buildingNumber}`}
                                  color="default"
                                  variant="outlined"
                                />
                              )}
                              {jobsite.communityName && (
                                <Chip
                                  size="small"
                                  label={jobsite.communityName}
                                  color="default"
                                  variant="outlined"
                                />
                              )}
                              {jobsite.aspirePropertyId && (
                                <Chip
                                  size="small"
                                  label={`Already matched to Property ${jobsite.aspirePropertyId}`}
                                  color="secondary"
                                  title="This jobsite is already linked to another Aspire property. Matching will override the existing link."
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <>
                              {jobsite.address && (
                                <Typography variant="body2" component="div">
                                  {jobsite.address}
                                </Typography>
                              )}
                              {(jobsite.city || jobsite.state || jobsite.zip) && (
                                <Typography variant="body2" component="div" color="textSecondary">
                                  {jobsite.city}{jobsite.city && (jobsite.state || jobsite.zip) ? ', ' : ''}{jobsite.state} {jobsite.zip}
                                </Typography>
                              )}
                              {jobsite.client && (
                                <Typography variant="caption" component="div" color="textSecondary">
                                  Customer: {jobsite.client.displayName || jobsite.client.companyName || 'Unknown'}
                                </Typography>
                              )}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              ) : (
                !jobsiteSearchLoading && (
                  <Alert severity="info">
                    No jobsites found matching "{jobsiteSearchQuery}". Try a different search term.
                  </Alert>
                )
              )}
            </Box>
          )}

          {jobsiteSearchQuery.length < 2 && jobsiteSearchQuery.length > 0 && (
            <Alert severity="info" style={{ marginTop: 16 }}>
              Please enter at least 2 characters to search
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPropertyMatchDialogOpen(false)} color="default">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contact Match Dialog */}
      <Dialog
        open={contactMatchDialogOpen}
        onClose={() => setContactMatchDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Match Contact to Portal Customer
          {selectedContact && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              Finding a match for: <strong>{selectedContact.ContactName || `${selectedContact.FirstName || ''} ${selectedContact.LastName || ''}`.trim() || selectedContact.Email}</strong>
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedContact && (
            <Box mb={3} p={2} style={{ backgroundColor: '#f5f5f5', borderRadius: 4 }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Contact Details:
              </Typography>
              <Typography variant="body2">
                <strong>Contact ID:</strong> {selectedContact.ContactID}
              </Typography>
              <Typography variant="body2">
                <strong>Name:</strong> {selectedContact.ContactName || `${selectedContact.FirstName || ''} ${selectedContact.LastName || ''}`.trim() || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Email:</strong> {selectedContact.Email || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Phone:</strong> {selectedContact.Phone || selectedContact.Mobile || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Company:</strong> {selectedContact.CompanyName || 'N/A'}
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            autoFocus
            label="Search Customers"
            placeholder="Type customer name or email..."
            value={contactCustomerSearchQuery}
            onChange={(e) => {
              const query = e.target.value;
              setContactCustomerSearchQuery(query);
              searchCustomersForContact(query);
            }}
            variant="outlined"
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: contactCustomerSearchLoading && (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              )
            }}
          />

          {contactCustomerSearchQuery.length >= 2 && (
            <Box mt={2}>
              {contactCustomerSearchResults.length > 0 ? (
                <>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Found {contactCustomerSearchResults.length} customer(s)
                  </Typography>
                  <List>
                    {contactCustomerSearchResults.map((customer) => (
                      <ListItem
                        key={customer._id}
                        button
                        onClick={() => handleContactMatch(customer)}
                        style={{
                          border: '1px solid #e0e0e0',
                          borderRadius: 4,
                          marginBottom: 8
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                              <Typography variant="subtitle1">
                                {customer.displayName || customer.companyName || customer.email || 'Unknown'}
                              </Typography>
                              {customer.aspireContactId && (
                                <Chip
                                  size="small"
                                  label={`Already matched to Contact ${customer.aspireContactId}`}
                                  color="secondary"
                                  title="This customer is already linked to another Aspire contact. Matching will override the existing link."
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <>
                              {customer.email && (
                                <Typography variant="body2" component="div">
                                  Email: {customer.email}
                                </Typography>
                              )}
                              {customer.phone && (
                                <Typography variant="body2" component="div">
                                  Phone: {customer.phone}
                                </Typography>
                              )}
                              {customer.address && (
                                <Typography variant="body2" component="div" color="textSecondary">
                                  {customer.address}
                                  {customer.city && `, ${customer.city}`}
                                  {customer.state && `, ${customer.state}`}
                                  {customer.zip && ` ${customer.zip}`}
                                </Typography>
                              )}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              ) : (
                !contactCustomerSearchLoading && (
                  <Alert severity="info">
                    No customers found matching "{contactCustomerSearchQuery}". Try a different search term or create a new customer.
                  </Alert>
                )
              )}
            </Box>
          )}

          {contactCustomerSearchQuery.length < 2 && contactCustomerSearchQuery.length > 0 && (
            <Alert severity="info" style={{ marginTop: 16 }}>
              Please enter at least 2 characters to search
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactMatchDialogOpen(false)} color="default">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Route Match Dialog */}
      <Dialog
        open={routeMatchDialogOpen}
        onClose={() => setRouteMatchDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Match Route to Crew
          {selectedRoute && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              Finding a crew for: <strong>{selectedRoute.RouteName || `Route ${selectedRoute.RouteID}`}</strong>
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedRoute && (
            <Box mb={3} p={2} style={{ backgroundColor: '#f5f5f5', borderRadius: 4 }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Route Details:
              </Typography>
              <Typography variant="body2">
                <strong>Route ID:</strong> {selectedRoute.RouteID}
              </Typography>
              <Typography variant="body2">
                <strong>Route Name:</strong> {selectedRoute.RouteName}
              </Typography>
              <Typography variant="body2">
                <strong>Crew Leader:</strong> {selectedRoute.CrewLeaderName || 'N/A'}
                {selectedRoute.CrewLeaderContactID && ` (Contact ID: ${selectedRoute.CrewLeaderContactID})`}
              </Typography>
              <Typography variant="body2">
                <strong>Division:</strong> {selectedRoute.DivisionName || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Crew Size:</strong> {selectedRoute.RouteSize || 'N/A'}
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            autoFocus
            label="Search Crews"
            placeholder="Type crew name, number, or foreman name..."
            value={crewSearchQuery}
            onChange={(e) => {
              const query = e.target.value;
              setCrewSearchQuery(query);
              searchCrews(query);
            }}
            variant="outlined"
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: crewSearchLoading && (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              )
            }}
          />

          {crewSearchQuery.length >= 1 && (
            <Box mt={2}>
              {crewSearchResults.length > 0 ? (
                <>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Found {crewSearchResults.length} crew(s)
                  </Typography>
                  <List>
                    {crewSearchResults.map((crew) => (
                      <ListItem
                        key={crew._id}
                        button
                        onClick={() => handleRouteMatch(crew)}
                        style={{
                          border: '1px solid #e0e0e0',
                          borderRadius: 4,
                          marginBottom: 8
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                              <Typography variant="subtitle1">
                                {crew.name || 'Unnamed Crew'}
                              </Typography>
                              {crew.crewNumber && (
                                <Chip
                                  size="small"
                                  label={`#${crew.crewNumber}`}
                                  color="primary"
                                  variant="outlined"
                                />
                              )}
                              {crew.division && (
                                <Chip
                                  size="small"
                                  label={crew.division}
                                  color="default"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <>
                              {crew.foremanName && (
                                <Typography variant="body2" component="div">
                                  Foreman: {crew.foremanName}
                                </Typography>
                              )}
                              {crew.foreman && (
                                <Typography variant="body2" component="div" color="textSecondary">
                                  Foreman: {crew.foreman.firstName} {crew.foreman.lastName}
                                </Typography>
                              )}
                              {crew.employees && crew.employees.length > 0 && (
                                <Typography variant="caption" component="div" color="textSecondary">
                                  {crew.employees.length} employee(s)
                                </Typography>
                              )}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              ) : (
                !crewSearchLoading && (
                  <Alert severity="info">
                    No crews found matching "{crewSearchQuery}". Try a different search term.
                  </Alert>
                )
              )}
            </Box>
          )}
          <Divider style={{ margin: '24px 0' }} />
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="body2" color="textSecondary">
              No matching crew? Create one from this route's info.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddCrewIcon />}
              onClick={() => selectedRoute && handleCreateCrewFromRoute(selectedRoute)}
              disabled={routeLoading}
            >
              Create New Crew
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRouteMatchDialogOpen(false)} color="default">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Create Crew from Route Dialog */}
      <Dialog
        open={createCrewDialogOpen}
        onClose={() => setCreateCrewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <AddCrewIcon style={{ marginRight: 8, color: '#516e76' }} />
            <Typography variant="h6">Create Crew from Route</Typography>
          </Box>
          {createCrewRoute && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
              Aspire Route: <strong>{createCrewRoute.RouteName}</strong> (ID: {createCrewRoute.RouteID})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Crew Name"
                variant="outlined"
                value={createCrewData.crewName}
                onChange={(e) => setCreateCrewData({ ...createCrewData, crewName: e.target.value })}
                helperText="This will be the crew name in the Portal"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Division</InputLabel>
                <Select
                  value={createCrewData.division}
                  onChange={(e) => setCreateCrewData({ ...createCrewData, division: e.target.value })}
                  label="Division"
                >
                  <MenuItem value="Maintenance">Maintenance</MenuItem>
                  <MenuItem value="Irrigation">Irrigation</MenuItem>
                  <MenuItem value="Herbicide">Herbicide</MenuItem>
                  <MenuItem value="Enhancement">Enhancement</MenuItem>
                  <MenuItem value="Tree">Tree</MenuItem>
                  <MenuItem value="Pools">Pools</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Foreman Name"
                variant="outlined"
                value={createCrewData.foremanName}
                onChange={(e) => setCreateCrewData({ ...createCrewData, foremanName: e.target.value })}
                helperText="Crew leader from Aspire (editable)"
              />
            </Grid>
          </Grid>
          <Box mt={2} p={2} bgcolor="#f5f5f5" borderRadius={8}>
            <Typography variant="body2" color="textSecondary">
              This will create a new crew in the Portal and automatically link it to this Aspire route.
              A crew number will be auto-generated.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateCrewDialogOpen(false)} disabled={routeLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmitCreateCrew}
            disabled={routeLoading || !createCrewData.crewName.trim()}
            startIcon={routeLoading ? <CircularProgress size={16} /> : <AddCrewIcon />}
          >
            {routeLoading ? 'Creating...' : 'Create Crew'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth
});

export default connect(mapStateToProps)(AspireSyncDashboard);
